import { Fragment, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AtSign, Bell, BellOff, FileText, MessagesSquare, Paperclip, ListTodo } from 'lucide-react';
import { useNavigate } from 'react-router';
import { api, get, getTodos, post } from '../nucleo/api';
import { useSessao } from '../nucleo/sessao';
import { Menu } from './menu';
import { dataHora, nomeDeArquivo, textoLivre, textoSemNomes } from '../nucleo/formato';
import { censurarCampo } from '../nucleo/censura-json';
import { nomesDoSistema, textoDoSistema, type ParametrosSistema } from '../nucleo/texto-sistema';
import { valorCru, rotuloCampo, rotuloEntidade, rotuloValor } from '../nucleo/rotulos';
import { Abas, PainelAba } from './abas';
import { Botao } from './base';
import { Carregando, Erro } from './estados';
import { useToast } from './toast';
import { mensagemDeErro } from '../nucleo/api';
import { AreaSensivel, useDefsExtras } from './formulario';
import { LinhaDoTempo, type MarcoLinha } from './chatter-linha';
import { modulosWeb } from '../nucleo/modulos-web';

export interface DadosChatter {
  mensagens: { id: string; tipo: 'mensagem' | 'nota' | 'historico' | 'sistema'; autor_id?: string | null; corpo: string; alteracoes: { campo: string; de: unknown; para: unknown }[] | null; parametros?: ParametrosSistema | null; created_at: string; autor_nome: string | null }[];
  anexos: { id: string; nome: string; mime: string; tamanho: number; created_at: string; autor_nome?: string | null }[];
  seguidores: { id: string; nome: string }[];
  seguindo: boolean;
  /** Fase 2 (W3, F1e): até quando eu li este chatter. */
  lido_em?: string | null;
}

/**
 * Lista de 30/10 (B), item 3.1: evento de outro registro na linha do tempo (`GET /chatter-agregado`): a mensagem como veio,
 * a entidade dela (para a censura do valor), a origem já pronta ("Negociação: ...", com link) e, no anexo, o texto do evento.
 */
export type EventoRelacionado = DadosChatter['mensagens'][number] & { entidade: string; origem: ReactNode; conteudo?: ReactNode };

/**
 * Lista de 30/10 (B), item 22: na linha do tempo cada arquivo do registro entra como evento, na data do envio, misturado aos
 * outros (como o histórico da empresa faz com os arquivos dos registros ligados), em vez de uma lista à parte no topo.
 * Função pura, testada.
 */
/**
 * Lista de 30/10 (B), lote C2 (item 10): o servidor grava "Arquivo anexado" no histórico (o fato fica depois que o arquivo sai). Na
 * linha do tempo o arquivo vivo já é evento (com o link, `eventosDosAnexos`): a linha de sistema dele sai, para não repetir; a
 * do arquivo removido fica, junto com "Arquivo removido".
 */
export function semAnexadoRepetido(d: DadosChatter): DadosChatter {
  const vivos = new Set(d.anexos.map((a) => a.id));
  const repetida = (m: DadosChatter['mensagens'][number]) => m.tipo === 'sistema' && m.parametros?.modelo === 'arquivoAnexado' && vivos.has(String(m.parametros.anexo_id));
  return d.mensagens.some(repetida) ? { ...d, mensagens: d.mensagens.filter((m) => !repetida(m)) } : d;
}

export function eventosDosAnexos(anexos: DadosChatter['anexos'], entidade: string, nomes: (string | null | undefined)[], anexou: string): EventoRelacionado[] {
  return anexos.map(
    (a): EventoRelacionado => ({
      id: `anexo-${a.id}`,
      tipo: 'sistema',
      corpo: '',
      alteracoes: null,
      parametros: null,
      created_at: a.created_at,
      autor_nome: a.autor_nome ?? null,
      entidade,
      origem: null,
      conteudo: (
        <>
          {anexou}{' '}
          <a href={`/api/v1/anexos/${a.id}`} className="text-acento-forte hover:underline" title={nomeDeArquivo(a.nome, ...nomes)} data-evento-anexo="">
            {nomeDeArquivo(a.nome, ...nomes)}
          </a>
        </>
      ),
    }),
  );
}

/**
 * Review rlb-1 (SPEC-M2): o histórico herdado (o chatter da negociação original até a cópia) como eventos só leitura: as
 * mensagens como vieram e os arquivos como eventos, sem a linha "Arquivo anexado" repetida. Função pura, testada.
 */
export function eventosHerdados(d: DadosChatter, entidade: string, nomes: (string | null | undefined)[], anexou: string): EventoRelacionado[] {
  return [...semAnexadoRepetido(d).mensagens.map((m): EventoRelacionado => ({ ...m, entidade, origem: null })), ...eventosDosAnexos(d.anexos, entidade, nomes, anexou)];
}

/** Mensagem de outra pessoa depois da minha última leitura ("não lida" por usuário). */
export const ehNova = (m: { tipo: string; autor_id?: string | null; created_at: string }, lidoEm: string | null | undefined, eu: string) =>
  !!lidoEm && (m.tipo === 'mensagem' || m.tipo === 'nota') && m.autor_id !== eu && m.created_at > lidoEm;


/**
 * Fase 2 (W15): "Discutir com a equipe". Abre (ou cria) no chat da equipe o canal de discussão do registro, com quem clica
 * como participante, e leva a ele. Só com o módulo Equipe instalado e a permissão de enviar; o servidor confere que a
 * pessoa vê o registro.
 */
function BotaoDiscutir({ entidade, registroId }: { entidade: string; registroId: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const navegar = useNavigate();
  const abrir = useMutation({
    mutationFn: () => post<{ id: string }>('/equipe/registros', { entidade, registro_id: registroId, titulo: rotuloEntidade(entidade), link: location.pathname + location.search }),
    onSuccess: (r) => navegar(`/comunicacao/equipe/chat?canal=${r.id}`),
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Botao variante="fantasma" tamanho="sm" data-discutir-equipe title={t('chatter.discutirAjuda')} carregando={abrir.isPending} onClick={() => abrir.mutate()} icone={<MessagesSquare className="size-4" />}>
      {t('chatter.discutir')}
    </Botao>
  );
}

/**
 * N10 e F7: painel de chatter reaproveitado por todos os módulos. Modo discreto (r10 visual M1, B3, B5): o valor do histórico
 * passa pela mesma censura da Auditoria (`censurarCampo` com a entidade) e pelo rótulo no idioma; mensagem, nota e texto de
 * sistema passam por `textoLivre`. `nomes`: nomes conhecidos do registro (parceiro, contatos) que viram iniciais dentro do
 * texto com a opção de nomes. `titulo`: nome ou número do registro, para as ações dos módulos (Fase 2, W23: o "Criar tarefa"
 * do Tarefas leva o título ao card, que só o mostra a quem vê o registro).
 */
export function Chatter({
  entidade,
  registroId,
  rotulosCampos,
  nomes = [],
  titulo,
  variante = 'cartoes',
  marco,
  relacionados,
  linkDoSistema,
  herdado,
  criarTarefa,
}: {
  entidade: string;
  registroId: string;
  rotulosCampos?: Record<string, string>;
  nomes?: (string | null | undefined)[];
  titulo?: string | null;
  /**
   * Lista de 02/11, item 6: no CRM/Comercial o "Criar tarefa" abre o formulário de tarefa do CRM (o `FormTarefa`), no lugar do
   * card do módulo Tarefas. A função abre o formulário; `null` esconde o botão (sem permissão ou negociação encerrada). Sem a
   * prop (os demais módulos), segue a ação do módulo Tarefas.
   */
  criarTarefa?: (() => void) | null;
  /**
   * Ajustes do CRM (27/10, F3 e C9): `linha` é o histórico em linha do tempo do RD (trilho, bolinha por evento, filtro
   * "Exibir" por tipo, "Criar anotação" que abre o campo, e a lixeira na própria anotação). Opt-in: sem ela, os cartões.
   */
  variante?: 'cartoes' | 'linha';
  /**
   * Na `linha`, o marco do fim com a bandeirola: "Negociação criada" e a data de criação do registro. Lista de 30/10 (B), item
   * 21: com `link`, o rótulo leva ao registro de origem ("Criada como cópia de <negociação>").
   */
  marco?: MarcoLinha;
  /**
   * Lista de 30/10 (B), item 3.1: na `linha`, eventos de outros registros ligados (os contatos e as negociações da empresa),
   * misturados na ordem do tempo, cada um com a origem. Só leitura (sem lixeira). Aditivo.
   */
  relacionados?: EventoRelacionado[];
  /** Na `linha`, o endereço que o evento de sistema abre (pelos parâmetros do servidor); null sem link. */
  linkDoSistema?: (p: ParametrosSistema) => string | null;
  /**
   * Review rlb-1 (SPEC-M2): na `linha`, o histórico herdado de outro registro (a negociação original da cópia) até `ate`,
   * abaixo do marco e só leitura. Quem não vê o registro de origem (404) fica sem os herdados.
   */
  herdado?: { entidade: string; registroId: string; ate: string } | null;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const chave = ['chatter', entidade, registroId];
  const q = useQuery({ queryKey: chave, queryFn: () => get<DadosChatter>(`/chatter/${entidade}/${registroId}`) });
  const qHerdado = useQuery({
    queryKey: ['chatter', herdado?.entidade, herdado?.registroId, 'ate', herdado?.ate],
    queryFn: () => get<DadosChatter>(`/chatter/${herdado!.entidade}/${herdado!.registroId}`, { ate: herdado!.ate }),
    enabled: variante === 'linha' && !!herdado,
    retry: false,
  });
  const [tipo, setTipo] = useState<'mensagem' | 'nota'>('mensagem');
  const [corpo, setCorpo] = useState('');
  const arquivo = useRef<HTMLInputElement>(null);
  const idAbas = useId();
  const sessao = useSessao();
  const eu = sessao.usuario.id;
  // Fase 2 (W3, F1e): @menção avisa (sino e push) quem vê o registro; o servidor descarta quem não vê.
  const [mencoes, setMencoes] = useState<Record<string, string>>({});
  const membros = useQuery({ queryKey: ['membros', 'todos'], queryFn: () => getTodos<{ id: string; nome: string }>('/membros'), staleTime: 300_000 });
  const mencionados = Object.entries(mencoes).filter(([, nome]) => corpo.includes(`@${nome}`));
  const lido = useRef(false);
  useEffect(() => {
    if (q.data && !lido.current) {
      lido.current = true;
      void post(`/chatter/${entidade}/${registroId}/lido`).catch(() => undefined);
    }
  }, [q.data, entidade, registroId]);

  const publicar = useMutation({
    mutationFn: () => post(`/chatter/${entidade}/${registroId}`, { tipo, corpo, mencoes: mencionados.map(([id]) => id), link: location.pathname + location.search }),
    onSuccess: () => {
      setCorpo('');
      setMencoes({});
      return qc.invalidateQueries({ queryKey: chave });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const seguir = useMutation({
    mutationFn: (s: boolean) => post(`/chatter/${entidade}/${registroId}/seguir`, { seguir: s }),
    onSuccess: () => qc.invalidateQueries({ queryKey: chave }),
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const anexar = useMutation({
    mutationFn: (f: File) => {
      const fd = new FormData();
      fd.append('entidade', entidade);
      fd.append('registro_id', registroId);
      fd.append('arquivo', f);
      return api('POST', '/anexos', fd);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: chave }),
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  // Review r15 spec M1: campo personalizado no histórico pela regra efetiva, com a definição da entidade (só quando há `extras`).
  const comExtras = !!q.data?.mensagens.some((m) => m.alteracoes?.some((a) => a.campo === 'extras' || a.campo.startsWith('extras.')));
  const defsExtras = useDefsExtras(comExtras ? entidade : undefined);
  const valorHistorico = (campo: string, v: unknown, ent: string = entidade) => textoSemNomes(rotuloValor(campo, censurarCampo(campo, v, ent, undefined, [], ent === entidade ? defsExtras : undefined)), ...nomes);

  if (q.isPending) return <Carregando />;
  if (q.isError) return <Erro erro={q.error} aoTentar={() => q.refetch()} />;
  const d = q.data;

  const cabecalho = (
    <>
      <div className="flex items-center justify-between gap-2">
        <h3 className="kicker">{t('chatter.titulo')}</h3>
        <div className="flex flex-wrap items-center justify-end gap-1">
          {sessao.instalado('equipe') && sessao.pode('equipe.enviar') && !entidade.startsWith('equipe.') && <BotaoDiscutir entidade={entidade} registroId={registroId} />}
          {/* Fase 2 (W23, K11): ações dos módulos instalados no registro (o "Criar tarefa" do Tarefas). */}
          {modulosWeb
            .filter((m) => m.acaoNoRegistro && sessao.instalado(m.slug) && (criarTarefa === undefined || m.slug !== 'tarefas'))
            .map((m) => {
              const Acao = m.acaoNoRegistro!;
              return <Acao key={m.slug} entidade={entidade} registroId={registroId} titulo={titulo} />;
            })}
          {criarTarefa && (
            <Botao variante="fantasma" tamanho="sm" data-criar-tarefa-crm="" onClick={criarTarefa} icone={<ListTodo className="size-4" />}>
              {t('chatter.criarTarefa')}
            </Botao>
          )}
          <Botao
            variante="fantasma"
            tamanho="sm"
            onClick={() => seguir.mutate(!d.seguindo)}
            icone={d.seguindo ? <BellOff className="size-4" /> : <Bell className="size-4" />}
          >
            {d.seguindo ? t('chatter.seguindo') : t('chatter.seguir')}
          </Botao>
        </div>
      </div>
    </>
  );
  const compositor = (
    <>
      <div className="rounded-xl border border-borda bg-sup-2 p-3">
        <Abas variante="compacto" className="mb-2" idBase={idAbas} rotulo={t('chatter.titulo')} ativa={tipo} aoMudar={setTipo} itens={(['mensagem', 'nota'] as const).map((x) => ({ chave: x, rotulo: t(`chatter.${x}`) }))} />
        <PainelAba idBase={idAbas} chave={tipo}>
          {/* Campo com borda funcional de 3:1 (r6 visual M3), como todo campo. */}
          <AreaSensivel
            aria-label={tipo === 'nota' ? t('chatter.escreverNota') : t('chatter.escrever')}
            rows={3}
            value={corpo}
            onChange={(e) => setCorpo(e.target.value)}
            placeholder={tipo === 'nota' ? t('chatter.escreverNota') : t('chatter.escrever')}
            className="w-full resize-y rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm placeholder:text-tinta-3 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco"
          />
        </PainelAba>
        {mencionados.length > 0 && <p className="mt-1 text-xs text-tinta-3">{t('notificacoes.chatter.mencionados', { nomes: mencionados.map(([, n]) => n).join(', ') })}</p>}
        {/* Review r16 visual B3: "Mencionar alguém" numa linha só (o invólucro do menu encolhia e o rótulo quebrava a 1440); sem
            espaço, os botões descem para a linha de baixo. */}
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <Menu
            className="shrink-0"
            rotulo={t('notificacoes.chatter.mencionar')}
            icone={<AtSign className="size-4" aria-hidden />}
            classePainel="left-0 w-60 max-h-64 overflow-y-auto"
            itens={(membros.data?.itens ?? [])
              .filter((m) => m.id !== eu)
              .map((m) => ({
                chave: m.id,
                rotulo: m.nome,
                aoClicar: () => {
                  setMencoes((x) => ({ ...x, [m.id]: m.nome }));
                  setCorpo((c) => `${c}${c && !c.endsWith(' ') ? ' ' : ''}@${m.nome} `);
                },
              }))}
          />
          <input ref={arquivo} type="file" className="hidden" onChange={(e) => e.target.files?.[0] && anexar.mutate(e.target.files[0])} />
          <Botao variante="fantasma" tamanho="sm" carregando={anexar.isPending} onClick={() => arquivo.current?.click()} icone={<Paperclip className="size-4" />}>
            {t('chatter.anexar')}
          </Botao>
          <Botao variante="secundario" tamanho="sm" disabled={!corpo.trim()} carregando={publicar.isPending} onClick={() => publicar.mutate()}>
            {t('comum.enviar')}
          </Botao>
        </div>
      </div>
    </>
  );
  const listaAnexos = (
    <>
      {d.anexos.length > 0 && (
        <div>
          <p className="kicker mb-2">{t('chatter.anexos')}</p>
          <ul className="flex flex-col gap-1">
            {d.anexos.map((a) => (
              <li key={a.id}>
                <a href={`/api/v1/anexos/${a.id}`} className="flex min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-sm text-tinta-2 hover:bg-sup-2 hover:text-tinta-1">
                  <FileText className="size-4 shrink-0" aria-hidden />
                  {/* Review r12 visual M2: o nome do arquivo pode trazer o nome da pessoa (também em slug) e documento ou valor. */}
                  <span className="truncate" title={nomeDeArquivo(a.nome, ...nomes)}>
                    {nomeDeArquivo(a.nome, ...nomes)}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
  if (variante === 'linha')
    return (
      <LinhaDoTempo d={semAnexadoRepetido(d)} eu={eu} entidade={entidade} registroId={registroId} nomes={nomes} rotulosCampos={rotulosCampos} valorHistorico={valorHistorico} marco={marco} linkDoSistema={linkDoSistema} cabecalho={cabecalho} compositor={compositor} anexos={null} relacionados={[...(relacionados ?? []), ...eventosDosAnexos(d.anexos, entidade, nomes, t('chatter.linha.anexou'))]} herdados={herdado && qHerdado.data ? eventosHerdados(qHerdado.data, herdado.entidade, nomes, t('chatter.linha.anexou')) : undefined} />
    );

  return (
    <section aria-label={t('chatter.titulo')} className="flex min-w-0 flex-col gap-4">
      {cabecalho}

      {compositor}

      {listaAnexos}

      <ol className="flex flex-col gap-3">
        {d.mensagens.length === 0 && <li className="text-sm text-tinta-3">{t('chatter.semAtividade')}</li>}
        {d.mensagens.map((m, k) => (
          <Fragment key={m.id}>
          {/* Fase 2 (W3, F1e): divisória entre as mensagens novas dos outros (desde a minha última leitura) e as já lidas. */}
          {k > 0 && ehNova(d.mensagens[k - 1]!, d.lido_em, eu) && !ehNova(m, d.lido_em, eu) && (
            <li data-chatter-novas className="flex items-center gap-2 text-xs font-medium text-acento-forte">
              <span className="h-px flex-1 bg-acento-forte" aria-hidden />
              {t('notificacoes.chatter.novas')}
              <span className="h-px flex-1 bg-acento-forte" aria-hidden />
            </li>
          )}
          <li className={`rounded-lg border px-3 py-2 text-sm ${m.tipo === 'nota' ? 'border-dourado' : 'border-borda'}`}>
            <p className="flex flex-wrap items-baseline justify-between gap-x-3 text-xs text-tinta-3">
              <span className="font-medium text-tinta-2">{m.autor_nome ?? 'MalgoOS'}</span>
              <span className="numero">{dataHora(m.created_at)}</span>
            </p>
            {m.tipo === 'historico' && m.alteracoes ? (
              <ul className="mt-1 flex flex-col gap-0.5 text-tinta-2">
                {m.alteracoes.map((a) => (
                  <li key={a.campo} className="break-words">
                    {t('chatter.historico')} <strong>{rotulosCampos?.[a.campo] ?? rotuloCampo(a.campo)}</strong> {t('chatter.de')} <span className={`text-tinta-3${valorCru(a.campo, a.de) ? ' numero' : ''}`}>{valorHistorico(a.campo, a.de)}</span> {t('chatter.para')}{' '}
                    <span className={valorCru(a.campo, a.para) ? 'numero' : undefined}>{valorHistorico(a.campo, a.para)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1 break-words whitespace-pre-wrap text-tinta-1">{m.tipo === 'sistema' ? textoLivre(textoDoSistema(m.corpo, m.parametros), ...nomes, ...nomesDoSistema(m.parametros)) : textoLivre(m.corpo, ...nomes)}</p>
            )}
          </li>
          </Fragment>
        ))}
      </ol>
    </section>
  );
}
